import { TitleCasePipe } from '@angular/common';
import { Component, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { NgxPrinterService } from 'ngx-printer';
import { ConfirmationService, MenuItem, MessageService } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Subscription } from 'rxjs';
import { AppComponent } from 'src/app/app.component';
import { IOrganization } from 'src/app/common/model/organization.model';
import { ORGANIZATION_PERMISSION_TYPE } from 'src/app/common/model/permission.model';
import { FileService } from 'src/app/common/services/file/file.service';
import { OrganizationService } from 'src/app/common/services/organization/organization.service';
import { UtilService } from 'src/app/common/services/util/util.service';
import { isEmpty } from 'src/app/common/utils';
import {
  ETransactionStatus,
  ITransactionHeader,
  ITransactionLineItem,
  ITransactionPaymentRecord,
  PaymentTerm
} from 'src/app/EBMSuite/models/transaction.model';
import { SalesService } from 'src/app/EBMSuite/services/sales/sales.service';
import { ProjectConstants } from 'src/assets/constants/project.constants';
import { ReceiptsPrintViewComponent } from '../../../ebmsuite-shared/components/receipts-print-view/receipts-print-view.component';
import { receiptStyling } from './invoiceStyling.const';

@Component({
  selector: 'app-invoice-details',
  templateUrl: './invoice-details.component.html',
  styleUrls: ['./invoice-details.component.scss'],
  providers: [MessageService, DialogService, ConfirmationService, TitleCasePipe]
})
export class InvoiceDetailsComponent implements OnInit {
  @ViewChild('print') print!: ElementRef;
  @ViewChild('receipt') receipt!: ElementRef;

  @ViewChild(ReceiptsPrintViewComponent, { read: ElementRef })
  PrintComponent!: ElementRef;

  subscription = new Subscription();
  ORGANIZATION_PERMISSION_TYPE = ORGANIZATION_PERMISSION_TYPE;

  invoiceDetailsHeader!: ITransactionHeader | null;
  invoicePaymentRecords!: ITransactionPaymentRecord[];
  invoiceLineItems: ITransactionLineItem[] = [];
  projectConstants = ProjectConstants;
  organizationLogo: any;
  organization!: IOrganization | null;
  receiptHtml = receiptStyling;
  signAndStamp: any = {};

  items: MenuItem[] = [
    {
      label: 'View receipt',
      icon: 'pi pi-eye'
    },
    {
      label: 'Delete',
      icon: 'pi pi-times'
    }
  ];

  addPaymentForm = new FormGroup({
    paidInCurrencyAmount: new FormControl(0, [Validators.required]),
    amount: new FormControl(0, [Validators.required]),
    paidInCurrency: new FormControl({ value: '', disabled: true }, [Validators.required]),
    paymentTerm: new FormControl('', [Validators.required])
  });
  paymentDialog = false;
  organizationPaymentTerms: any[] = [];
  defaultPaymentMethods = Object.keys(PaymentTerm);
  formattedCurrencyList: any[] = [];
  defaultCurrency!: { symbol: string; code: string };

  sendEmailDialog = false;
  sendToEmail = '';
  transactionStatus = Object.values(ETransactionStatus).filter(
    (status) =>
      status !== ETransactionStatus.PARTIALLYPAID && status !== ETransactionStatus.PAID
  );
  transactionStatusEnum = ETransactionStatus;
  selectedTransactionStatus = ETransactionStatus.DRAFT;

  ref: DynamicDialogRef | undefined;

  readonly app = inject(AppComponent);
  readonly activatedRoute = inject(ActivatedRoute);
  readonly salesService = inject(SalesService);
  readonly utilService = inject(UtilService);
  readonly fileService = inject(FileService);
  readonly organizationService = inject(OrganizationService);
  readonly messageService = inject(MessageService);
  readonly printerService = inject(NgxPrinterService);
  readonly dialogService = inject(DialogService);

  ngOnInit(): void {
    this.getInvoiceIdFromParam();
    this.getOrganization();
  }

  printTemplate() {
    this.printerService.printOpenWindow = false;
    this.printerService.renderClass = 'invoice-content';
    this.printerService.printHTMLElement(this.PrintComponent.nativeElement);
    this.printerService.printOpenWindow = true;
    this.printerService.renderClass = 'default';
  }

  getInvoiceIdFromParam(): void {
    this.subscription.add(
      this.activatedRoute.params.subscribe({
        next: (param) => {
          const invoiceId = param['invoiceId'];
          this.getInvoiceDetails(invoiceId);
        }
      })
    );
  }

  getInvoiceDetails(invoiceId: string): void {
    this.subscription.add(
      this.salesService.getInvoiceById(invoiceId).subscribe({
        next: (res) => {
          this.invoiceDetailsHeader = res.result.transactionHeader;
          this.selectedTransactionStatus =
            this.invoiceDetailsHeader.transactionStatus || ETransactionStatus.DRAFT;
          this.invoicePaymentRecords = res.result.transactionPaymentRecords;
          this.invoiceLineItems = res.result.transactionLineItems;
        }
      })
    );
  }
  triggerPrint() {
    this.print.nativeElement.click();
    const element = document.getElementById('invoice-content');
    if (element) {
      element.style.color = '#fff';
    }
    // this.utilService.goBack();
  }

  getOrganization(): void {
    this.subscription.add(
      this.organizationService.getCurrentOrganization().subscribe({
        next: (res) => {
          this.organization = res.result.organization;
          const currentOrganizationId = this.organization.currentLogoId;
          this.getOrgImage(currentOrganizationId);

          const { currentSignId, currentStampId } = res.result.organization;

          if (!isEmpty(currentSignId)) this.getImageFileById(currentSignId, 'sign');
          if (!isEmpty(currentStampId)) this.getImageFileById(currentStampId, 'stamp');
        }
      })
    );
  }

  getOrgImage(fileId: string): void {
    this.subscription.add(
      this.fileService.getImageFileById(fileId).subscribe({
        next: (res) => {
          this.createImageFromBlob(res);
        }
      })
    );
  }

  getImageFileById(fileId: string, key: 'sign' | 'stamp') {
    this.fileService.getImageFileById(fileId, 'Y').subscribe({
      next: (res) => {
        this.createSignAndStampFromBlob(res, key);
      }
    });
  }

  createSignAndStampFromBlob(image: Blob, key: 'sign' | 'stamp') {
    const reader = new FileReader();
    reader.addEventListener(
      'load',
      () => {
        this.signAndStamp[key] = reader.result;
      },
      false
    );

    if (image) {
      reader.readAsDataURL(image);
    }
  }

  createImageFromBlob(image: Blob) {
    const reader = new FileReader();
    reader.addEventListener(
      'load',
      () => {
        this.organizationLogo = reader.result;
      },
      false
    );

    if (image) {
      reader.readAsDataURL(image);
    } else {
      this.organizationLogo = null;
    }
  }

  sendEmail(): void {
    const email: any = {
      sendTo: this.sendToEmail.split(','),
      messageHeading: 'Invoice Receipt'
    };
    this.subscription.add(
      this.salesService
        .sendInvoiceEmail(this.invoiceDetailsHeader?._id, email)
        .subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'Email sent successfully'
            });
            this.sendEmailDialog = false;
          }
        })
    );
  }

  sendMailConfirmation() {
    this.sendEmailDialog = true;
    if (this.invoiceDetailsHeader?.customer?.email) {
      this.sendToEmail = this.invoiceDetailsHeader.customer.email;
    }
  }

  openPaymentDialog() {
    this.paymentDialog = true;
    this.getOrganizationPaymentMethods();
    this.getCurrency();

    const invoicePaymentData = {
      paidInCurrencyAmount: this.invoiceDetailsHeader?.convertedTransactionBalance,
      paidInCurrency: this.invoiceDetailsHeader?.currency
        ? this.invoiceDetailsHeader.currency.symbol
          ? this.invoiceDetailsHeader.currency.symbol
          : this.invoiceDetailsHeader.currency.code
        : this.utilService.getCurrentCurrency(),
      paymentTerm: ''
    };
    this.addPaymentForm.patchValue(invoicePaymentData);
  }

  getOrganizationPaymentMethods(): void {
    this.subscription.add(
      this.organizationService.getCurrentOrganization().subscribe({
        next: (res) => {
          if (res.result?.organization?.paymentTerms?.length) {
            this.organizationPaymentTerms =
              res.result?.organization?.paymentTerms?.length > 0
                ? res.result.organization.paymentTerms
                : this.defaultPaymentMethods;
          }
        }
      })
    );
  }
  getCurrency(): void {
    this.subscription.add(
      this.organizationService.getCurrencies().subscribe({
        next: (res) => {
          const currencyList = res.result;
          this.formattedCurrencyList = currencyList.map((currency) => ({
            ...currency,
            formattedLabel: `${currency.country} (${currency.code})`,
            currencySymbolOrCode: {
              symbol: currency.symbol ? currency.symbol : null,
              code: currency.code
            }
          }));
        }
      })
    );
  }

  addPayment(): void {
    const paymentRecord = {
      ...this.addPaymentForm.value,
      transaction: this.invoiceDetailsHeader?._id,
      paidInCurrency: this.invoiceDetailsHeader?.currency.code
    };
    this.subscription.add(
      this.salesService.appendTransactionPaymentRecord(paymentRecord).subscribe({
        next: (res) => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: res.message
          });
          if (this.invoiceDetailsHeader) {
            this.getInvoiceDetails(this.invoiceDetailsHeader._id!);
          }
          this.paymentDialog = false;
        },
        error: (err) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: err.message
          });
        }
      })
    );
  }

  approveInvoice(): void {
    const invoice = {
      ...this.invoiceDetailsHeader,
      transactionStatus: 'APPROVED'
    };
    this.subscription.add(
      this.salesService.updateInvoice(invoice, invoice._id).subscribe({
        complete: () => {
          if (this.invoiceDetailsHeader) {
            this.getInvoiceDetails(this.invoiceDetailsHeader._id!);
          }
        }
      })
    );
  }

  getTotalInvoiceAmountWithTax(totalAmount = 0, totalTaxAmount = 0): number {
    return totalAmount + totalTaxAmount;
  }

  updateTransaction(): void {
    const editedTransaction = {
      transactionStatus: this.selectedTransactionStatus
    };

    const updateTransactionRoute = this.salesService.updateInvoice(
      editedTransaction,
      this.invoiceDetailsHeader?._id
    );

    this.subscription.add(
      updateTransactionRoute.subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Invoice status updated successfully'
          });
          this.getInvoiceIdFromParam();
        },
        error: (err) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: err.error.message
          });
          this.getInvoiceIdFromParam();
        }
      })
    );
  }
}
